"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.Wallet = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _util = require("@polkadot/util");
var _sdkCore = require("@acala-network/sdk-core");
var _rxjs = require("rxjs");
var _operators = require("rxjs/operators");
var _ = require("..");
var _getMaxAvailableBalance = require("./utils/get-max-available-balance");
var _marketPriceProvider = require("./price-provider/market-price-provider");
var _oraclePriceProvider = require("./price-provider/oracle-price-provider");
var _types = require("./price-provider/types");
var _storages = require("./storages");
var _getChainType = require("../utils/get-chain-type");
var _dexPriceProvider = require("./price-provider/dex-price-provider");
var _aggregatePriceProvider = require("./price-provider/aggregate-price-provider");
var _acala = require("./balance-adapter/acala");
var _acala2 = require("./token-provider/acala");
var _did = require("./web3name/did");
var _getLiquidCrowdloanTokenPrice = require("./utils/get-liquid-crowdloan-token-price");
var _vesting = require("./vesting");
var _toPromise = require("./utils/to-promise");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; } /**
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          * wallet sdk support to query info about token list, token balance, token price and token value
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          */
class Wallet {
  constructor(api, configs) {
    var _this = this;
    this.api = void 0;
    // readed from chain information
    this.storages = void 0;
    this.configs = void 0;
    this.balanceAdapter = void 0;
    this.tokenProvider = void 0;
    this.priceProviders = void 0;
    // inject liquidity, homa sdk by default for easy using
    this.liquidity = void 0;
    this.homa = void 0;
    this.web3Name = void 0;
    this.isReady$ = void 0;
    this.consts = void 0;
    this.vesting = void 0;
    /**
     *  @name subscribeTokens
     *  @param type
     *  @description subscirbe the token list, can filter by type
     */
    this.subscribeTokens = (0, _util.memoize)(type => {
      return this.tokenProvider.subscribeTokens(type);
    });
    /**
     *  @name subscribeToken
     *  @description subscirbe the token info
     */
    this.subscribeToken = (0, _util.memoize)(target => {
      return this.tokenProvider.subscribeToken(target);
    });
    /**
     * @name subscribeBalance
     * @description subscribe `address` `token` balance information
     * @param token
     * @param address
     */
    this.subscribeBalance = (0, _util.memoize)((symbol, address) => {
      const token = this.tokenProvider.getToken(symbol);
      return this.balanceAdapter.subscribeBalance(token, address);
    });
    /**
     * @name subscribeIssuance
     * @description subscribe `token` issuance amount
     * @param token
     */
    this.subscribeIssuance = (0, _util.memoize)(symbol => {
      const token = this.tokenProvider.getToken(symbol);
      return this.balanceAdapter.subscribeIssuance(token);
    });
    /**
     * @name subscribeSuggestInput
     * @description subscirbe the suggest input amount for `account` `token`
     * @params token: Token
     * @params account: string
     */
    this.subscribeSuggestInput = (0, _util.memoize)((symbol, address, isAllowDeath, fee) => {
      const token = this.tokenProvider.getToken(symbol);
      return (0, _rxjs.combineLatest)({
        nativeToken: this.subscribeToken(this.consts.nativeCurrency),
        feeToken: this.subscribeToken(fee.currency)
      }).pipe((0, _operators.switchMap)(_ref => {
        let {
          nativeToken,
          feeToken
        } = _ref;
        const isNativeToken = (0, _sdkCore.forceToCurrencyName)(token) === nativeToken.name;
        const isFeeToken = (0, _sdkCore.forceToCurrencyName)(feeToken) === (0, _sdkCore.forceToCurrencyName)(token);
        return (0, _rxjs.combineLatest)({
          accountInfo: this.storages.nativeBalance(address).observable,
          tokenInfo: this.balanceAdapter.subscribeBalance(token, address),
          feeInfo: this.balanceAdapter.subscribeBalance(feeToken, address)
        }).pipe((0, _operators.map)(_ref2 => {
          let {
            accountInfo,
            tokenInfo,
            feeInfo
          } = _ref2;
          const providers = accountInfo.providers.toBigInt();
          const consumers = accountInfo.consumers.toBigInt();
          const nativeFreeBalance = _sdkCore.FixedPointNumber.fromInner(accountInfo.data.free.toString(), nativeToken.decimals);
          const nativeLockedBalance = _sdkCore.FixedPointNumber.fromInner((accountInfo.data.miscFrozen || accountInfo.data.frozen).toString(), nativeToken.decimals);
          const isDefaultFee = (0, _sdkCore.forceToCurrencyName)(feeToken) === nativeToken.name;
          const feeFreeBalance = isDefaultFee ? nativeFreeBalance : feeInfo.free;
          const targetFreeBalance = isNativeToken ? nativeFreeBalance : tokenInfo.free;
          const targetLockedBalance = isNativeToken ? nativeLockedBalance : tokenInfo.locked;
          const ed = token.ed;
          return (0, _getMaxAvailableBalance.getMaxAvailableBalance)({
            isDefaultFee,
            isFeeToken,
            isAllowDeath,
            providers,
            consumers,
            feeFreeBalance,
            targetFreeBalance,
            targetLockedBalance,
            ed,
            fee: fee.amount
          });
        }));
      }));
    });
    /**
     * @name subscribePrice
     * @description subscirbe the price of `token`
     */
    this.subscribePrice = (0, _util.memoize)(function (symbol, type) {
      var _presetTokens$liquidT;
      if (type === void 0) {
        type = _types.PriceProviderType.AGGREGATE;
      }
      const name = (0, _sdkCore.forceToCurrencyName)(symbol);
      const isDexShare = (0, _sdkCore.isDexShareName)(name);
      const presetTokens = _this.getPresetTokens();
      const isLiquidToken = (presetTokens === null || presetTokens === void 0 ? void 0 : (_presetTokens$liquidT = presetTokens.liquidToken) === null || _presetTokens$liquidT === void 0 ? void 0 : _presetTokens$liquidT.name) === name;
      const stakingToken = presetTokens.stakingToken;

      // if token is dex share, get dex share price form liquidity sdk
      if (isDexShare) {
        return _this.liquidity.subscribePoolDetails(name).pipe((0, _operators.map)(data => data.sharePrice), (0, _operators.catchError)(() => {
          // return 0 when error occured
          return (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
        }));
      }

      // get liquid token price when type price type isn't dex
      if (isLiquidToken && stakingToken && type !== _types.PriceProviderType.DEX) {
        return _this.homa.env$.pipe((0, _operators.filter)(env => !env.exchangeRate.isZero()), (0, _operators.switchMap)(env => _this.subscribePrice(stakingToken, type).pipe((0, _operators.map)(price => {
          return price.times(env.exchangeRate);
        }))));
      }

      // get liquid crowdloan token price when the price type is not dex
      if ((0, _sdkCore.isLiquidCrowdloanName)(name) && type !== _types.PriceProviderType.DEX) {
        return _this.subscribePrice(stakingToken, type).pipe((0, _operators.switchMap)(price => (0, _getLiquidCrowdloanTokenPrice.subscribeLiquidCrowdloanTokenPrice)(_this.api, price, (0, _sdkCore.getLiquidCrowdloanIdFromName)(name))));
      }

      // handle sa://0 as KSM, when chain is karuar
      if (_.ChainType.KARURA === (0, _getChainType.getChainType)(_this.consts.runtimeChain) && name === 'sa://0' && stakingToken) {
        return _this.subscribePrice(stakingToken, type);
      }
      const token = _this.tokenProvider.getToken(symbol);
      if (type === _types.PriceProviderType.MARKET && _this.priceProviders[_types.PriceProviderType.MARKET]) {
        var _this$priceProviders$;
        return ((_this$priceProviders$ = _this.priceProviders[_types.PriceProviderType.MARKET]) === null || _this$priceProviders$ === void 0 ? void 0 : _this$priceProviders$.subscribe(token)) || (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
      }
      if (type === _types.PriceProviderType.AGGREGATE && _this.priceProviders[_types.PriceProviderType.AGGREGATE]) {
        var _this$priceProviders$2;
        return ((_this$priceProviders$2 = _this.priceProviders[_types.PriceProviderType.AGGREGATE]) === null || _this$priceProviders$2 === void 0 ? void 0 : _this$priceProviders$2.subscribe(token)) || (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
      }
      if (type === _types.PriceProviderType.DEX && _this.priceProviders[_types.PriceProviderType.DEX]) {
        var _this$priceProviders$3;
        return ((_this$priceProviders$3 = _this.priceProviders[_types.PriceProviderType.DEX]) === null || _this$priceProviders$3 === void 0 ? void 0 : _this$priceProviders$3.subscribe(token)) || (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
      }
      if (type === _types.PriceProviderType.ORACLE && _this.priceProviders[_types.PriceProviderType.ORACLE]) {
        var _this$priceProviders$4;
        return ((_this$priceProviders$4 = _this.priceProviders[_types.PriceProviderType.ORACLE]) === null || _this$priceProviders$4 === void 0 ? void 0 : _this$priceProviders$4.subscribe(token)) || (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
      }
      return (0, _rxjs.of)(_sdkCore.FixedPointNumber.ZERO);
    });
    this.api = api;
    this.isReady$ = new _rxjs.BehaviorSubject(false);
    this.configs = _objectSpread({}, configs);
    this.tokenProvider = new _acala2.AcalaTokenProvider(this.api);
    this.init();
  }
  get isReady() {
    return (0, _rxjs.firstValueFrom)(this.isReady$.asObservable().pipe((0, _operators.filter)(i => i)));
  }
  init() {
    // 1. init constants
    this.initConsts();
    this.tokenProvider.isReady$.subscribe({
      next: status => {
        var _this$configs, _this$configs2;
        this.balanceAdapter = new _acala.AcalaBalanceAdapter({
          api: this.api,
          evmProvider: (_this$configs = this.configs) === null || _this$configs === void 0 ? void 0 : _this$configs.evmProvider
        });
        this.liquidity = new _.Liquidity(this.api, this);
        this.homa = new _.Homa(this.api, this);
        this.web3Name = new _did.DIDWeb3Name();
        const market = new _marketPriceProvider.MarketPriceProvider();
        const dex = new _dexPriceProvider.DexPriceProvider(this.liquidity);
        const aggregate = new _aggregatePriceProvider.AggregateProvider({
          market,
          dex
        });
        const oracle = new _oraclePriceProvider.OraclePriceProvider(this.api, {
          stakingToken: this.getPresetTokens(true).stableToken,
          tokenPrivoder: this.tokenProvider
        });
        this.priceProviders = _objectSpread({
          // default price provider
          [_types.PriceProviderType.AGGREGATE]: aggregate,
          [_types.PriceProviderType.MARKET]: market,
          [_types.PriceProviderType.ORACLE]: oracle,
          [_types.PriceProviderType.DEX]: dex
        }, (_this$configs2 = this.configs) === null || _this$configs2 === void 0 ? void 0 : _this$configs2.priceProviders);
        this.vesting = new _vesting.Vesting({
          api: this.api,
          tokenProvider: this.tokenProvider
        });
        this.storages = (0, _storages.createStorages)(this.api);
        this.isReady$.next(status);
      }
    });
  }
  initConsts() {
    this.consts = {
      runtimeChain: this.api.runtimeChain.toString(),
      nativeCurrency: this.api.registry.chainTokens[0].toString()
    };
  }
  async getTokens(type) {
    return (0, _rxjs.firstValueFrom)(this.subscribeTokens(type));
  }
  /**
   * @name __getToken
   * @deprecated change to `getToke`
   */
  __getToken(target) {
    return this.tokenProvider.getToken(target);
  }

  // direct get token no need await, must be called after wallet sdk is ready
  getToken(target) {
    return this.tokenProvider.getToken(target);
  }
  async getBalance(token, address) {
    return (0, _rxjs.firstValueFrom)(this.subscribeBalance(token, address));
  }
  async getIssuance(token) {
    return (0, _rxjs.firstValueFrom)(this.subscribeIssuance(token));
  }
  async getSuggestInput(token, address, isAllowDeath, fee) {
    return (0, _rxjs.firstValueFrom)(this.subscribeSuggestInput(token, address, isAllowDeath, fee));
  }
  getPresetTokens(ignoreCheck) {
    var _this$api$consts$homa, _this$api$consts$homa2, _this$api$consts$homa3, _this$api$consts$homa4;
    if (ignoreCheck === void 0) {
      ignoreCheck = false;
    }
    if (!ignoreCheck && this.isReady$.value === false) throw new _.SDKNotReady('wallet');
    const tokens = this.tokenProvider.getAllTokens();
    const data = {
      nativeToken: tokens[(0, _sdkCore.forceToCurrencyName)(this.consts.nativeCurrency)],
      stableToken: tokens[(0, _sdkCore.forceToCurrencyName)(this.api.consts.cdpEngine.getStableCurrencyId)],
      liquidToken: tokens[(0, _sdkCore.forceToCurrencyName)(((_this$api$consts$homa = this.api.consts.homa) === null || _this$api$consts$homa === void 0 ? void 0 : _this$api$consts$homa.liquidCurrencyId) || ((_this$api$consts$homa2 = this.api.consts.homaLite) === null || _this$api$consts$homa2 === void 0 ? void 0 : _this$api$consts$homa2.liquidCurrencyId) || '')],
      stakingToken: tokens[(0, _sdkCore.forceToCurrencyName)(((_this$api$consts$homa3 = this.api.consts.homa) === null || _this$api$consts$homa3 === void 0 ? void 0 : _this$api$consts$homa3.stakingCurrencyId) || ((_this$api$consts$homa4 = this.api.consts.homaLite) === null || _this$api$consts$homa4 === void 0 ? void 0 : _this$api$consts$homa4.stakingCurrencyId) || '')]
    };
    return data;
  }
  getPrice(token, type) {
    return (0, _rxjs.firstValueFrom)(this.subscribePrice(token, type));
  }
  async checkTransfer(address, currency, amount, direction) {
    if (direction === void 0) {
      direction = 'to';
    }
    const {
      nativeToken
    } = this.getPresetTokens();
    const token = this.getToken(currency);
    const isNativeToken = nativeToken.isEqual(token);
    const accountInfo = await (0, _toPromise.toPromise)(this.api.query.system.account(address));
    const balance = await this.getBalance(currency, address);
    let needCheck = true;

    // always check ED if the direction is `to`
    if (direction === 'to' && balance.free.add(amount).lt(token.ed || _sdkCore.FixedPointNumber.ZERO)) {
      throw new _.BelowExistentialDeposit(address, token);
    }
    if (direction === 'from') {
      if (isNativeToken) {
        needCheck = !(accountInfo.consumers.toBigInt() === BigInt(0));
      } else {
        needCheck = !(accountInfo.providers.toBigInt() > BigInt(0) || accountInfo.consumers.toBigInt() === BigInt(0));
      }
      if (needCheck && balance.free.minus(amount).lt(token.ed || _sdkCore.FixedPointNumber.ZERO)) {
        throw new _.BelowExistentialDeposit(address, token);
      }
    }
    return true;
  }
}
exports.Wallet = Wallet;